import { RenderedPdf, useRenderPdfFromDocuments } from './useRenderPdfFromDocuments.query'
import { useCallback } from 'react'
import { isPrescription } from '../../../misc/documents.utilities'
import { DocumentInstance } from '../../../model/DocumentInstance'
import { DocumentCategoryKeys } from '@follow/cdk'
import { CustomFile, RenderableFileExtension } from '../../../model/File'
import { isRenderableFileExtension } from '../../../misc/files.utilities'
import { useDirectPrintFile } from '../files'
import { addInfo, addWarning } from '../../../store/message'
import { useActionDispatch } from '../../utils'
import { usePrintingContext } from '../../../core/hooks/usePrintingContext'
import { usePrintablePdf } from '../../../core/hooks/usePritablePdfContext'

type PrintableDocument = Pick<DocumentInstance, 'id' | 'type'> & { category: DocumentCategoryKeys }
type PrintableFile = Pick<CustomFile, 'id' | 'extension'>

export type PrintableDocumentsVariable = {
  documents?: PrintableDocument[]
  files?: PrintableFile[]
}

export const usePrintDocuments = () => {
  const { isLoading, currentItems, error, startPrinting, finishPrinting, setError, resetError } =
    usePrintingContext()

  const dispatchInfo = useActionDispatch(addInfo)
  const dispatchError = useActionDispatch(addWarning)

  const { printBlob: printPdfBlob } = usePrintablePdf()

  const printBlob = useCallback(
    (renderedPdf: RenderedPdf) => printPdfBlob(renderedPdf.blob),
    [printPdfBlob],
  )

  const { render } = useRenderPdfFromDocuments({ forPrint: true })
  const directPrint = useDirectPrintFile()

  const print = useCallback(
    async ({ documents = [], files = [] }: PrintableDocumentsVariable) => {
      if (documents.length === 0 && files.length === 0) {
        return
      }
      const itemsToPrint = [
        ...documents.map((d) => ({
          kind: 'document' as const,
          id: d.id,
        })),
        ...files.map((f) => ({
          kind: 'file' as const,
          id: f.id,
        })),
      ]
      startPrinting(itemsToPrint)
      setError(null)

      dispatchInfo(
        'Impression en cours',
        `Merci de patienter, ${itemsToPrint?.length >= 2 ? 'vos documents sont en cours de préparation et seront prêts sous peu.' : 'votre document est en cours de préparation et sera prêt sous peu.'}`,
      )

      try {
        // Cas spécial de l'impression d'un seul fichier de type PDF -> pas d'appel au Renderer
        if (documents.length === 0 && files.length === 1 && files[0].extension === 'pdf') {
          directPrint({ fileId: files[0].id })
          return
        }

        const documentsToLock = documents
          .filter(({ category, type }) => type === 'farte' && category && isPrescription(category))
          .map(({ id }) => id)
        await new Promise<void>((resolve, reject) => {
          render(
            {
              documents: documents.map((document) => ({
                documentId: document.id,
                type: document.type,
                documentCategoryId: document.category,
              })),
              files: files
                .filter((file) => isRenderableFileExtension(file.extension))
                .map((file) => ({
                  fileId: file.id,
                  extension: file.extension as RenderableFileExtension,
                })),
              documentsToLock,
            },
            {
              onSuccess: (rendered) => {
                try {
                  printBlob(rendered)
                  resolve()
                } catch (e) {
                  reject(e)
                }
              },
              onError: (e: unknown) => reject(e),
            },
          )
        })
      } catch (e) {
        setError(e)
        dispatchError(
          'Impression interrompue',
          'Une erreur est survenue lors de la génération du document. Veuillez réessayer ou contacter le support si le problème persiste.',
        )
        finishPrinting()
        // throw e // let callers handle if they want
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      directPrint,
      printBlob,
      render,
      dispatchError,
      dispatchInfo,
      startPrinting,
      finishPrinting,
      setError,
    ],
  )

  return { print, isLoading, error, resetError, currentItems }
}
